/**
 * Customer Portal — public page where a customer enters their deal code
 * and sees real-time status of their deal.
 *
 * Route: /portal  (and /portal/:code)
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, Circle, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import { fmtMoney } from "../config";

export default function CustomerPortal() {
  const { code: urlCode } = useParams();
  const nav = useNavigate();
  const [code, setCode] = useState(urlCode || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const lookup = async (c) => {
    setLoading(true); setErr(""); setData(null);
    try {
      const { data: r } = await api.get(`/portal/deal/${c.toUpperCase()}`);
      setData(r);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't find that deal.");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (urlCode) lookup(urlCode); /* eslint-disable-next-line */ }, [urlCode]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    nav(`/portal/${code.toUpperCase()}`);
  };

  return (
    <main className="min-h-screen bg-[var(--ssc-paper)]">
      <Seo title="Customer Deal Portal · Scott Sells Chevys" description="Track your deal status with Scott in real time." noindex />
      <header className="bg-[var(--ssc-navy)] text-white px-4 sm:px-8 py-6">
        <div className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-[var(--ssc-blue)]">Customer Portal</div>
        <h1 className="text-2xl sm:text-3xl font-black mt-1">Your Deal Status</h1>
        <p className="text-white/70 text-sm">Enter the deal code Scott gave you to see where things stand.</p>
      </header>

      <section className="px-4 sm:px-8 py-8 max-w-3xl mx-auto">
        <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-[var(--ssc-line)] p-5 flex gap-2 flex-wrap" data-testid="portal-form">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
            placeholder="e.g. SSC-AB7K"
            className="ssc-input font-mono flex-1 min-w-[200px]"
            data-testid="portal-code-input"
          />
          <button type="submit" className="ssc-btn ssc-btn-primary text-sm" data-testid="portal-submit">
            {loading ? <Loader2 size={14} className="animate-spin" /> : "Look Up Deal"}
          </button>
        </form>

        {err && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="portal-err">{err}</div>}

        {data && (
          <div className="mt-6 space-y-4" data-testid="portal-deal">
            <div className="bg-white rounded-2xl border border-[var(--ssc-line)] p-5">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Deal {data.deal.code}</div>
              <div className="text-xl font-black text-[var(--ssc-navy)] mt-0.5">{data.deal.title || "Your deal"}</div>
              {data.deal.estimate?.monthly && (
                <div className="text-sm text-[var(--ssc-ink)] mt-1">Estimated payment: <span className="font-extrabold text-[var(--ssc-blue)]">{fmtMoney(data.deal.estimate.monthly)}/mo</span></div>
              )}
              {data.deal.vehicle?.year && (
                <div className="text-sm text-[var(--ssc-ink-2)] mt-1">{data.deal.vehicle.year} {data.deal.vehicle.make} {data.deal.vehicle.model} {data.deal.vehicle.trim || ""}</div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-[var(--ssc-line)] p-5">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-ink-2)] mb-3">Deal Progress</div>
              <ol className="space-y-3" data-testid="portal-stages">
                {data.stages.map((s, i) => (
                  <li key={s.id} className="flex items-start gap-3" data-testid={`portal-stage-${s.id}`}>
                    {s.done ? (
                      <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0"><Check size={14} /></div>
                    ) : (
                      <div className="w-7 h-7 rounded-full border-2 border-[var(--ssc-line)] text-[#6b7c93] flex items-center justify-center shrink-0"><Circle size={10} /></div>
                    )}
                    <div className="flex-1">
                      <div className={`font-extrabold ${s.done ? "text-[var(--ssc-navy)]" : "text-[#6b7c93]"}`}>{s.label}</div>
                      {!s.done && i > 0 && data.stages[i - 1].done && (
                        <div className="text-xs text-[var(--ssc-blue)] inline-flex items-center gap-1 mt-0.5"><ArrowRight size={11} /> Up next</div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="bg-[var(--ssc-blue)]/5 border border-[var(--ssc-blue)]/20 rounded-2xl p-4 text-sm text-[var(--ssc-navy)]">
              <div className="flex items-center gap-2 mb-1"><ShieldCheck size={14} className="text-[var(--ssc-blue)]" /><span className="font-bold">Private to you</span></div>
              Anyone with this deal code can see this status — keep it to yourself. Questions? Text Scott directly.
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
